import { isEmpty, isNil } from 'lodash/fp';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type AudiCalculationModuleSettingDataFragment } from '../../../api/fragments/AudiCalculationModuleSettingData';
import { type AudiCalculationModuleSpecsFragment } from '../../../api/fragments/AudiCalculationModuleSpecs';
import { useGetFinanceProductsQuery } from '../../../api/queries/getFinanceProducts';
import { useGetInsuranceProductsQuery } from '../../../api/queries/getInsuranceProducts';
import ArrayField from '../../../components/fields/ArrayField';
import TransferField from '../../../components/fields/TransferField';
import CollapsibleWrapper, { Panel } from '../../../components/wrappers/CollapsibleWrapper';
import useTranslatedString from '../../../utilities/useTranslatedString';

type RecordType = {
    key: string;
    title: string;
};

type FormSetting = {
    id: string;
    financeProductSuiteIds: string[];
    insuranceProductSuiteIds: string[];
};

export type FormValues = {
    settings: FormSetting[];
};

type SectionProps = {
    module: AudiCalculationModuleSpecsFragment;
    setting: AudiCalculationModuleSettingDataFragment;
    prefix: string;
};

const filterOption = (inputValue: string, option: RecordType) =>
    option.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1;

const FinanceProductsSection = ({ module, setting, prefix }: SectionProps) => {
    const { t } = useTranslation('audiCalculationDetails');
    const translatedString = useTranslatedString();

    const { data } = useGetFinanceProductsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { moduleId: setting.bankModuleId } },
        skip: isNil(setting.bankModuleId),
    });

    const dataSource = useMemo<RecordType[]>(() => {
        if (isEmpty(data?.result)) {
            return [];
        }

        return data.result.map(financeProduct => ({
            key: financeProduct.versioning.suiteId,
            title: translatedString(financeProduct.legalName),
        }));
    }, [data?.result, translatedString]);

    return (
        <CollapsibleWrapper defaultActiveKey={`${prefix}.financeProductSuiteIds-panel`}>
            <Panel
                key={`${prefix}.financeProductSuiteIds-panel`}
                header={t('audiCalculationDetails:panels.financeProductAssignment', {
                    displayName: setting.displayName,
                })}
            >
                <TransferField
                    dataSource={dataSource}
                    filterOption={filterOption}
                    listStyle={{ width: '100%' }}
                    name={`${prefix}.financeProductSuiteIds`}
                    render={item => item.title}
                />
            </Panel>
        </CollapsibleWrapper>
    );
};

const InsuranceSection = ({ module, setting, prefix }: SectionProps) => {
    const { t } = useTranslation('audiCalculationDetails');
    const translatedString = useTranslatedString();

    const { data } = useGetInsuranceProductsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter: { moduleId: setting.insuranceModuleId } },
        skip: isNil(setting.insuranceModuleId),
    });

    const dataSource = useMemo<RecordType[]>(() => {
        if (isEmpty(data?.result)) {
            return [];
        }

        return data.result.map(insurer => ({
            key: insurer.versioning.suiteId,
            title: translatedString(insurer.legalName),
        }));
    }, [data?.result, translatedString]);

    return dataSource.length ? (
        <CollapsibleWrapper defaultActiveKey={`${prefix}.insuranceProductSuiteIds-panel`}>
            <Panel
                key={`${prefix}.insuranceProductSuiteIds-panel`}
                header={t('audiCalculationDetails:panels.insuranceAssignment', { displayName: setting.displayName })}
            >
                <TransferField
                    dataSource={dataSource}
                    filterOption={filterOption}
                    listStyle={{ width: '100%' }}
                    name={`${prefix}.insuranceProductSuiteIds`}
                    render={item => item.title}
                />
            </Panel>
        </CollapsibleWrapper>
    ) : null;
};

export type AudiCalculationManagementSettingProps = {
    module: SectionProps['module'];
    name: string;
};

const AudiCalculationManagementSetting = ({ module, name }: AudiCalculationManagementSettingProps) => {
    const settings = useMemo(() => {
        if (!module.audiCalculationSettings) {
            return {};
        }

        return module.audiCalculationSettings.reduce(
            (acc, item) => ({
                ...acc,
                [item.id]: item,
            }),
            {} as { [key: string]: AudiCalculationModuleSettingDataFragment }
        );
    }, [module.audiCalculationSettings]);

    return (
        <ArrayField<FormSetting, FormValues>
            name={name}
            render={(arrayHelper, value) =>
                value?.map((setting, index) => {
                    const foundSetting = settings[setting.id];

                    if (!foundSetting) {
                        return null;
                    }

                    return (
                        <Fragment key={setting.id}>
                            <FinanceProductsSection
                                module={module}
                                prefix={`${name}[${index}]`}
                                setting={foundSetting}
                            />
                            <InsuranceSection module={module} prefix={`${name}[${index}]`} setting={foundSetting} />
                        </Fragment>
                    );
                })
            }
        />
    );
};

export default AudiCalculationManagementSetting;
